import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Link, useLocation, Navigate } from 'react-router-dom';
import {
    Accordion,
    Container,
    Row,
    Col,
    Card,
    Spinner,
    Badge,
    ListGroup,
} from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';

import { useUserApi } from '@/shared/services/userApi';
import { useEcosystemApi } from '@/shared/services/ecosystemApi';
import { useEcosystem } from '@/shared/contexts/EcosystemContext';
import useFullAuth from '@/features/auth/hooks/useFullAuth';

import IdentitySummary from './IdentitySummary';
import YourEcosystems from './YourEcosystems';
import EcosystemMembershipAccordion from './EcosystemMembershipAccordion';

// Lazy load the graph component
const PersonSubgraph = lazy(
    () => import('@/features/graph/components/PersonSubgraph')
);

/**
 * ProfilePage: the user's per-ecosystem profile, at `/{slug}/profile`.
 *
 * TENANT ONLY (#261). This is the `EcosystemMembership` tier of the two-tier
 * person model; the `User` tier lives on AccountPage at the platform apex
 * `/account`. Reaching `/profile` with no tenant resolved redirects there,
 * because every editable surface below depends on tenant-scoped endpoints
 * that reject platform-mode requests.
 *
 * Structure:
 *   1. IdentitySummary: platform identity (name, photo, bio), READ ONLY, with
 *      a link to the Account page. Read-only because that record is owned at
 *      the platform level; a second editor here would be two authoritative
 *      copies of one fact.
 *   2. My Organizations: orgs the user owns / has pending claims for.
 *      Tenant-scoped because organizations belong to a single ecosystem.
 *   3. YourEcosystems: join / leave control + membership index. Deliberately
 *      present on both this page and Account: it is the cross-ecosystem
 *      switcher, not a duplicate of the accordions below.
 *   4. Per-membership accordions: one per active EcosystemMembership. The
 *      accordion for the active ecosystem is expanded by default; others show
 *      a "Switch to {ecosystem.name}" CTA because cross-tenant inline editing
 *      would require running each accordion's API calls under a different
 *      basePath than the page's singleton.
 *   5. Network graph (PersonSubgraph) for the active ecosystem.
 */
export default function ProfilePage() {
    const [profile, setProfile] = useState(null);
    const [myOrganizations, setMyOrganizations] = useState([]);
    const [myClaims, setMyClaims] = useState([]);
    const [memberships, setMemberships] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [orgImageErrors, setOrgImageErrors] = useState({});

    const userApi = useUserApi();
    const ecosystemApi = useEcosystemApi();
    const { ecosystem: activeEcosystem, isPlatform } = useEcosystem();
    const { user: currentUser } = useFullAuth();
    const location = useLocation();

    // Handle fragment scroll after page loads
    useEffect(() => {
        if (location.hash && !loading) {
            // Small delay to ensure DOM is ready (especially for lazy-loaded components)
            const timeoutId = setTimeout(() => {
                const element = document.getElementById(location.hash.slice(1));
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                }
            }, 100);
            return () => clearTimeout(timeoutId);
        }
    }, [location.hash, loading]);

    useEffect(() => {
        // At the apex this page redirects (see the isPlatform branch below), so
        // skip the four requests whose results would be discarded. React runs
        // effects even when the render returns early, so the check is needed
        // here as well as in the render body.
        if (isPlatform) return;
        const fetchProfile = async () => {
            try {
                const [profileData, orgsData, claimsData, membershipsData] =
                    await Promise.all([
                        userApi.fetchProfile(),
                        userApi
                            .fetchMyOrganizations()
                            .catch(() => ({ organizations: [] })),
                        userApi.fetchMyClaims().catch(() => ({ claims: [] })),
                        ecosystemApi
                            .fetchMyMemberships()
                            .catch(() => ({ memberships: [] })),
                    ]);
                setProfile(profileData);
                setMyOrganizations(orgsData.organizations || []);
                setMyClaims(claimsData.claims || []);
                setMemberships(membershipsData.memberships || []);
            } catch (err) {
                setError('Failed to load profile');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, [userApi, ecosystemApi, isPlatform]);

    // No tenant resolved: send the user to the platform account page. Same
    // origin and basename, so this is an in-app navigation rather than a page
    // load. `replace` swaps the history entry so Back does not return here and
    // immediately forward again.
    if (isPlatform) {
        return <Navigate to="/account" replace />;
    }

    if (loading) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    const refreshProfile = () => {
        userApi.fetchProfile().then(setProfile).catch(console.error);
    };

    if (error) {
        return (
            <Container className="py-5 text-center">
                <p className="text-danger">{error}</p>
            </Container>
        );
    }

    // In tenant mode the accordion for the active ecosystem opens by default,
    // so its mentor / affiliation forms are visible immediately. Other
    // memberships collapse and offer a "Switch to {name}" CTA. In platform
    // mode (apex) there is no active membership to expand, the user is
    // expected to pick from YourEcosystems and we route them per-tenant.
    const activeMembership = memberships.find(
        (m) => activeEcosystem && m.ecosystem.slug === activeEcosystem.slug
    );
    const defaultAccordionKey = activeMembership
        ? activeMembership.ecosystem.slug
        : null;

    return (
        <>
            <Helmet>
                <title>My Profile | RILE Connect</title>
            </Helmet>
            <Container className="py-5">
                <Row className="justify-content-center">
                    <Col md={10} lg={8}>
                        {/* 1. Cross-ecosystem identity, READ ONLY. Editing
                            lives on the platform Account page: this page only
                            ever renders inside a tenant, and a second editable
                            copy of a platform-owned record is the duplicate-
                            ownership hazard (#261). */}
                        <IdentitySummary profile={profile} />

                        {/* 2. My Organizations (tenant-scoped, only meaningful
                            when an ecosystem is resolved, but harmless to show
                            at apex where the lists will be empty). */}
                        {(myOrganizations.length > 0 ||
                            myClaims.length > 0) && (
                            <Card className="shadow-sm mt-4">
                                <Card.Header className="bg-white border-bottom">
                                    <strong>My Organizations</strong>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <ListGroup variant="flush">
                                        {myOrganizations.map((org) => (
                                            <ListGroup.Item
                                                key={org._id}
                                                className="d-flex justify-content-between align-items-center"
                                            >
                                                <Link
                                                    to={`/organizations/${org._id}`}
                                                    className="text-decoration-none"
                                                >
                                                    <div className="d-flex align-items-center gap-3">
                                                        {org.imageUrl &&
                                                        !orgImageErrors[
                                                            org._id
                                                        ] ? (
                                                            <img
                                                                src={
                                                                    org.imageUrl
                                                                }
                                                                alt={org.name}
                                                                className="rounded org-thumbnail-sm"
                                                                onError={() =>
                                                                    setOrgImageErrors(
                                                                        (
                                                                            prev
                                                                        ) => ({
                                                                            ...prev,
                                                                            [org._id]: true,
                                                                        })
                                                                    )
                                                                }
                                                            />
                                                        ) : (
                                                            <div className="rounded bg-light d-flex align-items-center justify-content-center org-placeholder-sm">
                                                                <span className="text-muted">
                                                                    {org.name.charAt(
                                                                        0
                                                                    )}
                                                                </span>
                                                            </div>
                                                        )}
                                                        <div>
                                                            <strong>
                                                                {org.name}
                                                            </strong>
                                                            <br />
                                                            <small className="text-muted">
                                                                {
                                                                    org.primarySector
                                                                }
                                                            </small>
                                                        </div>
                                                    </div>
                                                </Link>
                                                <Badge
                                                    bg={
                                                        org.userPermissionRole ===
                                                        'owner'
                                                            ? 'primary'
                                                            : org.userPermissionRole ===
                                                                'admin'
                                                              ? 'info'
                                                              : 'secondary'
                                                    }
                                                >
                                                    {org.userPermissionRole}
                                                </Badge>
                                            </ListGroup.Item>
                                        ))}

                                        {/* Pending Claims */}
                                        {myClaims.map((claim) => (
                                            <ListGroup.Item
                                                key={claim.claimId}
                                                className="d-flex justify-content-between align-items-center"
                                            >
                                                <div className="d-flex align-items-center gap-3">
                                                    {claim.organization
                                                        .imageUrl &&
                                                    !orgImageErrors[
                                                        `claim_${claim.claimId}`
                                                    ] ? (
                                                        <img
                                                            src={
                                                                claim
                                                                    .organization
                                                                    .imageUrl
                                                            }
                                                            alt={
                                                                claim
                                                                    .organization
                                                                    .name
                                                            }
                                                            className="rounded org-thumbnail-sm"
                                                            onError={() =>
                                                                setOrgImageErrors(
                                                                    (prev) => ({
                                                                        ...prev,
                                                                        [`claim_${claim.claimId}`]: true,
                                                                    })
                                                                )
                                                            }
                                                        />
                                                    ) : (
                                                        <div className="rounded bg-light d-flex align-items-center justify-content-center org-placeholder-sm">
                                                            <span className="text-muted">
                                                                {claim.organization.name.charAt(
                                                                    0
                                                                )}
                                                            </span>
                                                        </div>
                                                    )}
                                                    <div>
                                                        <strong>
                                                            {
                                                                claim
                                                                    .organization
                                                                    .name
                                                            }
                                                        </strong>
                                                        <br />
                                                        <small className="text-muted">
                                                            Ownership claim
                                                            pending
                                                        </small>
                                                    </div>
                                                </div>
                                                <Badge bg="warning">
                                                    Pending
                                                </Badge>
                                            </ListGroup.Item>
                                        ))}
                                    </ListGroup>
                                </Card.Body>
                            </Card>
                        )}

                        {/* 3. Your Ecosystems: join / leave manager */}
                        <YourEcosystems />

                        {/* 4. Per-membership accordions: TENANT MODE ONLY.
                            At apex, the user picks an ecosystem from
                            YourEcosystems above; per-ecosystem state is
                            edited on that tenant's /profile page. */}
                        {!isPlatform && memberships.length > 0 && (
                            <Card className="shadow-sm mt-4" id="memberships">
                                <Card.Header className="bg-white border-bottom">
                                    <strong>Your Ecosystem Profiles</strong>
                                    <div className="text-muted small mt-1">
                                        Mentor profile and affiliations for each
                                        ecosystem you belong to. Each
                                        ecosystem&apos;s profile is independent.
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <Accordion
                                        defaultActiveKey={defaultAccordionKey}
                                        flush
                                    >
                                        {memberships.map((m) => (
                                            <EcosystemMembershipAccordion
                                                key={m.ecosystem.slug}
                                                membership={m}
                                                isActive={
                                                    activeEcosystem &&
                                                    m.ecosystem.slug ===
                                                        activeEcosystem.slug
                                                }
                                                profile={profile}
                                                onProfileUpdate={refreshProfile}
                                                eventKey={m.ecosystem.slug}
                                            />
                                        ))}
                                    </Accordion>
                                </Card.Body>
                            </Card>
                        )}

                        {/* 5. Network Graph Visualization: tenant-scoped */}
                        {!isPlatform && currentUser?._id && (
                            <div id="network-visualization">
                                <Suspense
                                    fallback={
                                        <Card className="border-0 shadow-sm mt-4">
                                            <Card.Body className="p-4 text-center">
                                                <Spinner
                                                    animation="border"
                                                    variant="primary"
                                                    size="sm"
                                                />
                                                <p className="mt-2 mb-0 small text-muted">
                                                    Loading network
                                                    visualization...
                                                </p>
                                            </Card.Body>
                                        </Card>
                                    }
                                >
                                    <PersonSubgraph
                                        userId={currentUser._id}
                                        userName={`${profile?.name?.first || ''} ${profile?.name?.last || ''}`}
                                        height={300}
                                    />
                                </Suspense>
                            </div>
                        )}
                    </Col>
                </Row>
            </Container>
        </>
    );
}
